import { useAuth } from "@/contexts/auth/AuthContext";
import responsive from "@/lib/static/responsive";
import { getVideoHostUrl } from "@/lib/utils";
import ArrowRight from "../icons/ArrowRight";
import {
  StyledButton,
  ButtonTypes,
  ButtonSizes,
  IconTypes,
} from "../StyledButton";

export const ActivityLessonPlanPreview = () => {
  const { openLoginPrompt, user } = useAuth();
  const isLoggedIn = !!user;

  return (
    <div className="flex flex-col gap-y-16 px-4 medium:gap-y-6 medium:px-6">
      <div className="mx-auto flex max-w-[548px] flex-col gap-y-8 text-center opacity-100 medium:max-w-[1156px] medium:gap-y-12">
        <h2 className="text-4xl leading-tight medium:mb-2 medium:text-[64px] medium:tracking-[-1px]">
          XQ Competency Navigator
        </h2>
        <p className="font-roobert text-xl leading-none text-dark-color medium:text-2xl medium:font-medium">
          Easily integrate essential cognitive, social, and emotional skills
          into instruction.
        </p>
        <p className="leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
          The XQ Competency Navigator offers educators practical tools and
          resources for putting competencies into practice in any subject area
          or learning environment.
        </p>
      </div>

      <div className="mx-auto max-w-[548px] medium:grid medium:max-w-[1156px] medium:grid-cols-2 medium:items-center medium:gap-4 large:gap-6">
        <div className="medium:order-last" aria-hidden="true">
          <video
            poster="/assets/homepage/activities.png"
            width={566}
            height={566}
            autoPlay
            muted
            playsInline
            loop
          >
            <source
              media={`(min-width:${responsive.small}px)`}
              src={`${getVideoHostUrl()}/2024/10/activities.mp4`}
              type="video/mp4"
            />
          </video>
        </div>
        <div className="flex flex-col items-center text-center medium:items-start medium:pl-6 medium:pr-4 medium:text-left large:pr-6">
          <h2 className="mb-6 font-roobert font-medium leading-none medium:mb-8 medium:text-xl">
            Activities
          </h2>
          <h3 className="mb-8 text-4xl medium:text-[64px] medium:leading-none">
            Download classroom-ready activities
          </h3>
          <p className="mb-8 leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
            Explore our collection of ready-to-use activities aligned with
            specific<>&nbsp;</>competencies.
          </p>
          <div className="flex gap-2">
            {!isLoggedIn && (
              <StyledButton
                buttonType={ButtonTypes.Primary}
                size={ButtonSizes.Medium}
                iconType={IconTypes.Right}
                onClick={() => openLoginPrompt("HP_activities")}
                className="w-fit medium:h-16 medium:pl-6 medium:pr-4"
              >
                <span className="hidden medium:inline">
                  Sign in to get started
                </span>
                <span className="medium:hidden">Get started</span>
                <ArrowRight fill="#FFFFFF" />
              </StyledButton>
            )}
            <StyledButton
              buttonType={
                isLoggedIn ? ButtonTypes.Primary : ButtonTypes.Secondary
              }
              size={ButtonSizes.Medium}
              iconType={IconTypes.Right}
              href="/activities"
              as="link"
              className={`w-fit medium:h-16 medium:pl-6 medium:pr-4 ${
                !isLoggedIn ? "bg-white" : ""
              }`}
            >
              Explore activities
              <ArrowRight fill={isLoggedIn ? "#FFFFFF" : "#145758"} />
            </StyledButton>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[548px] medium:grid medium:max-w-[1156px] medium:grid-cols-2 medium:items-center medium:gap-4 large:gap-6">
        <div aria-hidden="true">
          <video
            poster="/assets/homepage/saved_resources.png"
            width={566}
            height={566}
            autoPlay
            muted
            playsInline
            loop
          >
            <source
              media={`(min-width:${responsive.small}px)`}
              src={`${getVideoHostUrl()}/2024/12/saved_resources.mp4`}
              type="video/mp4"
            />
          </video>
        </div>
        <div className="flex flex-col items-center text-center medium:items-start medium:px-4 medium:text-left large:px-6">
          <h2 className="mb-6 font-roobert font-medium leading-none medium:mb-8 medium:text-xl">
            Saved resources
          </h2>
          <h3 className="mb-8 text-4xl medium:text-[64px] medium:leading-none">
            Collect & curate favorite resources
          </h3>
          <p className="mb-8 leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
            Save XQ Competencies and activities to your saved resources page,
            ensuring quick and easy access whenever you need them.
          </p>
          {isLoggedIn ? (
            <StyledButton
              buttonType={ButtonTypes.Primary}
              size={ButtonSizes.Medium}
              iconType={IconTypes.Right}
              as="link"
              href="/account/saved-resources/component-skills"
              className="w-fit medium:h-16 medium:pl-6 medium:pr-4"
            >
              View saved resources
              <ArrowRight fill="#FFFFFF" />
            </StyledButton>
          ) : (
            <StyledButton
              buttonType={ButtonTypes.Primary}
              size={ButtonSizes.Medium}
              iconType={IconTypes.Right}
              onClick={() => openLoginPrompt("HP_saved_resources")}
              className="w-fit medium:h-16 medium:pl-6 medium:pr-4"
            >
              <span className="hidden medium:inline">
                Sign in to get started
              </span>
              <span className="medium:hidden">Get started</span>
              <ArrowRight fill="#FFFFFF" />
            </StyledButton>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[548px] medium:grid medium:max-w-[1156px] medium:grid-cols-2 medium:items-center medium:gap-4 large:gap-6">
        <div className="medium:order-last" aria-hidden="true">
          <video
            poster="/assets/homepage/lesson_plans.png"
            width={566}
            height={566}
            autoPlay
            muted
            playsInline
            loop
          >
            <source
              media={`(min-width:${responsive.small}px)`}
              src={`${getVideoHostUrl()}/2024/10/lesson_plans.mp4`}
              type="video/mp4"
            />
          </video>
        </div>
        <div className="flex flex-col items-center text-center medium:items-start medium:px-4 medium:text-left large:px-6">
          <h2 className="mb-6 font-roobert font-medium leading-none medium:mb-8 medium:text-xl">
            Projects
          </h2>
          <h3 className="mb-8 text-4xl medium:text-[64px] medium:leading-none">
            Build learning experiences that matter
          </h3>
          <p className="mb-8 leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
            Enhance existing curriculum and plan new skills-driven projects in
            any content area.
          </p>
          {isLoggedIn ? (
            <StyledButton
              buttonType={ButtonTypes.Primary}
              size={ButtonSizes.Medium}
              iconType={IconTypes.Right}
              as="link"
              href="/account/projects"
              className="w-fit medium:h-16 medium:pl-6 medium:pr-4"
            >
              View projects
              <ArrowRight fill="#FFFFFF" />
            </StyledButton>
          ) : (
            <StyledButton
              buttonType={ButtonTypes.Primary}
              size={ButtonSizes.Medium}
              iconType={IconTypes.Right}
              onClick={() => openLoginPrompt("HP_lesson_planner")}
              className="w-fit medium:h-16 medium:pl-6 medium:pr-4"
            >
              <span className="hidden medium:inline">
                Sign in to get started
              </span>
              <span className="medium:hidden">Get started</span>
              <ArrowRight fill="#FFFFFF" />
            </StyledButton>
          )}
        </div>
      </div>
    </div>
  );
};
